import { PropsWithChildren, useEffect, useRef } from 'react';

import styled from '@emotion/styled';

import { useGetMyProfile } from 'services/hooks/user';
import { useGetUsersIdentityVerifications } from 'services/hooks/verification';

import CircularLoading from 'components/loading/CircularLoading';

/**
 * 리워드 페이지 접근 게이트.
 * 한국 크리에이터(성인·국내 본인인증)만 접근 가능. 미조건 유저는 빈 페이지 + 시스템 alert.
 *
 * 조건: isCreator ∧ isAdult ∧ countryCode === 'KR' ∧ 본인인증 countryCode === 'KR'.
 */
export default function RewardAccessGate({ children }: PropsWithChildren) {
  const { data: profile, isFetched: isProfileFetched } = useGetMyProfile();
  const { data: identity, isFetched: isIdentityFetched } = useGetUsersIdentityVerifications();

  const isReady = isProfileFetched && isIdentityFetched;
  const isAccessible =
    !!profile?.isCreator && !!profile?.isAdult && profile?.countryCode === 'KR' && identity?.countryCode === 'KR';

  const alertedRef = useRef(false);

  useEffect(() => {
    if (isReady && !isAccessible && !alertedRef.current) {
      alertedRef.current = true;
      window.alert('리워드 이용 대상자가 아닙니다.');
    }
  }, [isReady, isAccessible]);

  // 로딩 중 리워드 콘텐츠 플래시 방지: 흰 화면 대신 중앙 스피너
  if (!isReady)
    return (
      <SLoadingWrapper>
        <CircularLoading />
      </SLoadingWrapper>
    );
  if (!isAccessible) return <></>; // 빈 페이지

  return <>{children}</>;
}

const SLoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 60vh;
`;
